import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { UseSnCode } from 'utils/useSnCode'
import { toBuddhistYear } from 'utils/app.utils'
import { dateToView } from 'utils/date.utils'

type AppliedFilters = {
    branchId: number
    appointmentStatus: string
    dateStart: string | null
    dateEnd: string | null
    search: string
}

type Branch = { branchId: number; branchName?: string; branchNameEn?: string }

type SummaryBucket = {
    paid?: number | string
    cancelBill?: number | string
    overdue?: number | string
    cancelVisit?: number | string
}

type VisitExportShape = {
    rows: any[]
    sum?: SummaryBucket
    total?: SummaryBucket
}

type Props = {
    data: VisitExportShape
    applied: AppliedFilters
    branches: Branch[]
    t: (key: string) => string
}

const THIN_BORDER = {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const }
}

const colLetter = (n: number) => {
    let s = ''
    let x = n
    while (x > 0) {
        const m = (x - 1) % 26
        s = String.fromCharCode(65 + m) + s
        x = Math.floor((x - 1) / 26)
    }
    return s
}

const exportVisitListToExcel = async (props: Props) => {
    const { data, applied, branches, t } = props
    const snType = await UseSnCode()

    /** Column blueprint — kept in one list so the SN-code column drop is a single splice
     *  in two places (header + each row), mirroring the on-screen VisitList behaviour. */
    type ColumnSpec = {
        labelKey: string
        width: number
        align: 'left' | 'center' | 'right'
        valueOf: (row: any) => string | number
        numFmt?: string
        isText?: boolean
    }

    const moneyCell = (cancelled: boolean, n: any): number | string =>
        cancelled ? '-' : (Number(n) || 0)

    const columns: ColumnSpec[] = [
        { labelKey: 'REPORT.TABLE.VISIT.DATE', width: 14, align: 'left', isText: true, valueOf: (r) => dateToView(r.updatedAt) || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.VN', width: 14, align: 'left', valueOf: (r) => r.vnNumber || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.CN_NUMBER', width: 14, align: 'left', valueOf: (r) => r.cnNumber || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.BRANCH_CN_NUMBER', width: 16, align: 'left', valueOf: (r) => r.branchCnNumber || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.SN_CODE', width: 14, align: 'left', valueOf: (r) => r.snCode || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.PATIENT_NAME', width: 24, align: 'left', valueOf: (r) => r.fullName || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.PHONE', width: 14, align: 'left', valueOf: (r) => r.phone || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_TOTAL', width: 14, align: 'right', numFmt: '#,##0.00', valueOf: (r) => moneyCell(r.visitStatus === 'CANCEL_VISIT', r.totalPrice) },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_PAY', width: 14, align: 'right', numFmt: '#,##0.00', valueOf: (r) => moneyCell(r.visitStatus === 'CANCEL_VISIT', r.amountPaid) },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_OVERDUE', width: 14, align: 'right', numFmt: '#,##0.00', valueOf: (r) => moneyCell(r.visitStatus === 'CANCEL_VISIT', r.overdue) },
        { labelKey: 'REPORT.TABLE.VISIT.APPOINTMENT_STATUS', width: 16, align: 'center', valueOf: (r) => t(`REPORT.VISIT_STATUS.${r.visitStatus}`) },
        { labelKey: 'REPORT.TABLE.VISIT.SEND_BY', width: 18, align: 'center', valueOf: (r) => r.updatedBy || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.CANCEL_BY', width: 18, align: 'center', valueOf: (r) => r.cancelBy || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.REMARK_CANCEL', width: 22, align: 'center', valueOf: (r) => r.billRemark || '-' },
        { labelKey: 'REPORT.TABLE.VISIT.TRANSACTION_BY', width: 18, align: 'center', valueOf: (r) => r.visitStatus === 'CANCEL_VISIT' ? '-' : (r.updatedBillBy || '-') }
    ]
    if (!snType) columns.splice(4, 1)

    const workbook = new ExcelJS.Workbook()
    const sheetName = t('REPORT.REPORT_NAME.VISIT')
    const sheet = workbook.addWorksheet(sheetName)

    const lastColLetter = colLetter(columns.length)

    /* ---------- Row 1 : title ---------- */
    const titleRow = sheet.addRow([sheetName])
    titleRow.font = { bold: true, size: 16 }
    titleRow.alignment = { horizontal: 'center' }
    sheet.mergeCells(`A1:${lastColLetter}1`)

    /* ---------- Row 2 : applied-filter chip ---------- */
    const branchLabel = applied.branchId === 0
        ? t('REPORT.REPORT_GROUP.ALL')
        : (branches.find((b) => b.branchId === applied.branchId)?.branchName || '-')
    const statusLabel = applied.appointmentStatus === 'ALL'
        ? t('REPORT.FILTER.ALL')
        : t(`REPORT.VISIT_STATUS.${applied.appointmentStatus}`)
    const dStart = applied.dateStart ? toBuddhistYear(moment(applied.dateStart), 'DD/MM/YYYY') : '-'
    const dEnd = applied.dateEnd ? toBuddhistYear(moment(applied.dateEnd), 'DD/MM/YYYY') : '-'
    const filterInfo = `${t('REPORT.FILTER.BRANCH')}: ${branchLabel}    ` +
        `${t('REPORT.FILTER.APPOINTMENT_STATUS')}: ${statusLabel}    ` +
        `${t('REPORT.FILTER.START')}: ${dStart}    ${t('REPORT.FILTER.DATE_END')}: ${dEnd}`
    const filterRow = sheet.addRow([filterInfo])
    filterRow.font = { size: 11 }
    sheet.mergeCells(`A2:${lastColLetter}2`)

    /* ---------- Row 3 : header ---------- */
    const headerRow = sheet.addRow(columns.map((c) => t(c.labelKey)))
    headerRow.font = { bold: true, size: 11 }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
    headerRow.height = 22
    headerRow.eachCell((cell) => {
        cell.border = THIN_BORDER
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } }
    })

    /* ---------- Data rows ---------- */
    const rows = data?.rows ?? []
    for (const row of rows) {
        const xlRow = sheet.addRow(columns.map((c) => c.valueOf(row)))
        xlRow.eachCell((cell, colIdx) => {
            const spec = columns[colIdx - 1]
            cell.border = THIN_BORDER
            cell.alignment = { horizontal: spec.align, vertical: 'middle', wrapText: true }
            if (spec.numFmt && typeof cell.value === 'number') cell.numFmt = spec.numFmt
            if (spec.isText) cell.numFmt = '@'
        })
    }

    /* ---------- Sum-of-page row (mirrors VisitList footer) ---------- */
    if (rows.length) {
        const sumTotalPrice = rows.reduce((acc, r) => acc + (r.visitStatus === 'CANCEL_VISIT' ? 0 : (Number(r.totalPrice) || 0)), 0)
        const sumPaid = rows.reduce((acc, r) => acc + (r.visitStatus === 'CANCEL_VISIT' ? 0 : (Number(r.amountPaid) || 0)), 0)
        const sumOverdue = rows.reduce((acc, r) => acc + (r.visitStatus === 'CANCEL_VISIT' ? 0 : (Number(r.overdue) || 0)), 0)
        const labelColspan = snType ? 7 : 6
        const sumRowValues: (string | number)[] = new Array(columns.length).fill('')
        sumRowValues[0] = t('REPORT.TITLE.SUM')
        sumRowValues[labelColspan] = sumTotalPrice
        sumRowValues[labelColspan + 1] = sumPaid
        sumRowValues[labelColspan + 2] = sumOverdue
        const sumRow = sheet.addRow(sumRowValues)
        sumRow.font = { bold: true }
        sheet.mergeCells(`A${sumRow.number}:${colLetter(labelColspan)}${sumRow.number}`)
        sumRow.eachCell((cell, colIdx) => {
            cell.border = THIN_BORDER
            const spec = columns[colIdx - 1]
            cell.alignment = { horizontal: spec?.align || 'left', vertical: 'middle' }
            if (typeof cell.value === 'number') cell.numFmt = '#,##0.00'
        })
    }

    /* ---------- Column widths ---------- */
    columns.forEach((c, i) => {
        sheet.getColumn(i + 1).width = c.width
    })

    /* ---------- Spacer + appointment-status summary table (mirrors VisitTotal) ---------- */
    sheet.addRow([])
    sheet.addRow([])

    const totalHeaders = [
        t('REPORT.TABLE.VISIT.APPOINTMENT_STATUS'),
        t('REPORT.VISIT_STATUS.PAID'),
        t('REPORT.VISIT_STATUS.CANCEL_BILL'),
        t('REPORT.VISIT_STATUS.OVERDUE'),
        t('REPORT.VISIT_STATUS.CANCEL_VISIT')
    ]
    const totalHeaderRow = sheet.addRow(totalHeaders)
    totalHeaderRow.font = { bold: true, size: 11 }
    totalHeaderRow.alignment = { horizontal: 'center' }
    totalHeaderRow.eachCell((cell) => {
        cell.border = THIN_BORDER
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } }
    })

    const addSummaryRow = (label: string, bucket: SummaryBucket | undefined, isMoney: boolean) => {
        if (!bucket) return
        const numOrDash = (v: any): number | string => {
            if (v === undefined || v === null || v === '') return '-'
            const n = Number(v)
            return isFinite(n) ? n : '-'
        }
        const row = sheet.addRow([
            label,
            numOrDash(bucket.paid),
            numOrDash(bucket.cancelBill),
            numOrDash(bucket.overdue),
            isMoney ? '-' : numOrDash(bucket.cancelVisit)
        ])
        row.eachCell((cell, colIdx) => {
            cell.border = THIN_BORDER
            cell.alignment = { horizontal: colIdx === 1 ? 'left' : 'right', vertical: 'middle' }
            if (colIdx > 1 && typeof cell.value === 'number') cell.numFmt = '#,##0.00'
        })
    }

    addSummaryRow(t('REPORT.UNIT.QTY'), data?.sum, false)
    addSummaryRow(t('REPORT.UNIT.QTY_PAYMENT'), data?.total, true)

    /* ---------- Save ---------- */
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const fileSuffix = applied.dateStart && applied.dateEnd ? ` (${applied.dateStart}-${applied.dateEnd})` : ''
    saveAs(blob, `${sheetName}${fileSuffix}.xlsx`)
}

export default exportVisitListToExcel
